/**
 * modal wrapper component
 * Contain card component
 * <modal #nomdelamodale >
 *     <span  class="modal-header-content">
 *     </span>
 *     <span  class="modal-body-content">
 *     </span>
 *     <span  class="modal-footer-content">
 *     </span>
 * </modal>
 */
import { DOCUMENT } from '@angular/common';
import {
    Component, Input, Inject, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, Renderer2
} from '@angular/core';

@Component({
    selector: 'modal',
    template: `
        <ng-container *ngIf="showModal">
            <div class="modal" #overlay
                [ngClass]="{'tooltip-mode': isTooltip}"
                (click)="onOverlayClick($event)">
                <card [isScrollable]="true">
                    <div class="card-header-content">
                        <ng-content select=".modal-header-content"></ng-content>
                    </div>

                    <div class="card-sub-header-content">
                        <ng-content select=".modal-sub-header-content"></ng-content>
                    </div>

                    <div class="card-body-content"
                        [class.fixHeight]="fixHeight">
                        <ng-content select=".modal-body-content"></ng-content>
                    </div>

                    <div class="card-footer-content">
                        <ng-content select=".modal-footer-content"></ng-content>
                    </div>
                </card>
            </div>
        </ng-container>
    <div *ngIf="!isTooltip" class="modal-mask"></div>
    `,
    styles: [`
        .modal{
            position: fixed;
            top: 0;
            width: 100%;
            height: 100%;
            z-index: 10000;
            left: 0;
            border: 1px solid black;
            background: rgba(0, 0, 0, .6);
            display: flex;
            justify-content: center;
            align-items: baseline;
        }
        .modal>card{
            overflow: auto;
            overflow-x: hidden;
            background-color: white;
            margin-top: 10%;
            max-height: 80%;
        }
    `]
})

export class ModalComponent implements AfterViewInit, OnDestroy {
    @Input()
    public hideModal: boolean;

    @Input()
    public showModal: boolean;

    @Input()
    public fixHeight: boolean;

    @Input()
    public clickOutOverride: boolean = false;

    @Input()
    public allowClickOut: boolean = true;

    @Input()
    public isTooltip: boolean = false;

    @Input()
    public appendTo: string;

    public firstOpen: boolean = true;

    public clickOutAlt: boolean = false;

    @Output()
    public readonly onClose: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    public readonly onCheckClose: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('overlay')
    public overlay: ElementRef;

    private readonly onKeyupHandler: any;

    constructor(
        public elRef: ElementRef,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly renderer: Renderer2,
    ) {
        this.onKeyupHandler = this.onKeyup.bind(this);
    }


    public ngAfterViewInit(): void {
        // Removes the component from its parent and adds it to the body
        // In order to display it correctly
        // /!\ BEWARE : when destroying the component, it must be removed from the body
        if (this.appendTo === "body") {
            document.body.appendChild(this.elRef.nativeElement);
        }
    }

    public ngOnDestroy(): void {
        if (this.appendTo === "body") {
            document.body.removeChild(this.elRef.nativeElement);
        }

        document.removeEventListener('keyup', this.onKeyupHandler);
    }

    public onOverlayClick(event: Event): void {
        if (event.target === this.overlay.nativeElement) {
            this.hide();
        }
    }

    public hide(): void {
        if (this.allowClickOut) {
            if (this.showModal) {
                this.onClose.emit();
                this.simpleHide();
            }
        }
    }

    public simpleHide(): void {
        this.showModal = false;
        document.removeEventListener('keyup', this.onKeyupHandler);
        this.onClose.emit(this.showModal);
        if (!this.isTooltip) {
            this.renderer.setStyle(this.document.body, 'overflow', 'auto');
        }
    }

    public show(fixHeight: boolean = false): void {
        this.showModal = true;

        // This event must only be registered when the modal is shown
        // Otherwise, a whole bunch of events listeners will be registered
        // on the document, when all the components of a page will be created
        // but not shown
        // Which creates performance issues (in hybrid apps at least (angularJS + angular))
        document.addEventListener('keyup', this.onKeyupHandler);

        if (fixHeight) {
            this.fixHeight = fixHeight;
        } else {
            this.fixHeight = false;
            this.onClose.emit(this.showModal);
        }

        if (!this.isTooltip) {
            this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        }
    }

    public setStyle(style?: object): void {
        Object.keys(style || {})
            .map((key: string) => ({ key, value: style[key] }))
            .forEach(({ key, value }: { key: string; value: string }) => this.renderer.setStyle(this.elRef.nativeElement, key, value));
    }

    private onKeyup(event: KeyboardEvent): void {
        if (event.keyCode === 27) {
            if (this.allowClickOut) {
                this.hide();
                this.onClose.emit(this.showModal);
            } else {
                this.onCheckClose.emit(this.showModal);
            }
        }
    }
}
